import React, { type ReactNode, forwardRef } from 'react';
import { styled } from '@compiled/react';
import type { VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip, { type TooltipPrimitiveProps, TooltipPrimitive } from '@atlaskit/tooltip';
import { gridSize, borderRadius, layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import type { DataBasic } from '@atlassian/jira-platform-services-user-current/src/types';
import { FireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type {
	logsTableOfTabs_logEdges$key,
	logsTableOfTabs_logEdges$data,
} from '@atlassian/jira-relay/src/__generated__/logsTableOfTabs_logEdges.graphql';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import type { User } from '@atlassian/jira-shared-types/src/rest/jira/user.tsx';
import { messages } from './messages';
import { NoDataEnableOutgoingMailInfo } from './no-data-enable-outgoing-mail-info';
import { NoRecords } from './no-records';
import { getLogDateTime, ErrorMessage, getNotificationName } from './utils';

type OutgoingMailConfigType = {
	enabled: boolean;
	canEditConfig: boolean;
};

type OutgoingMailConfigDataType = {
	outgoingMailConfig: OutgoingMailConfigType | null;
	error: string | null;
	loading: boolean;
};

type LogsTablePropsType<
	TScrollElement extends Element = Element,
	TItemElement extends Element = Element,
> = {
	virtualizer: Virtualizer<TScrollElement, TItemElement>;
	allEdges: logsTableOfTabs_logEdges$key;
	projectKey: string;
	outgoingMailConfigData: OutgoingMailConfigDataType;
	selectedTab: string;
};

type ShowTablePropsType<
	TScrollElement extends Element = Element,
	TItemElement extends Element = Element,
> = {
	data: logsTableOfTabs_logEdges$data;
	virtualizer: Virtualizer<TScrollElement, TItemElement>;
	projectKey: string;
	outgoingMailConfigData: OutgoingMailConfigDataType;
	selectedTab: string;
};

const issueFailuresTab = 'ISSUE_NOTIFICATION';

const rowRenderer = (
	virtualRow: VirtualItem,
	edgeData: logsTableOfTabs_logEdges$data,
	formatMessage: FormatMessage,
	projectKey: string,
	selectedTab: string,
	timeZone: string,
) => {
	const virtualRowIndex = virtualRow.index;
	if (!edgeData[virtualRowIndex]) {
		return (
			<TableRow
				data-index={virtualRowIndex}
				height={virtualRow.size}
				translateY={virtualRow.start}
				key={virtualRowIndex}
			>
				<TableCellSmall selectedTab={selectedTab} />
				<TableCellSmall selectedTab={selectedTab} />
				<TableCellMedium selectedTab={selectedTab}>
					{formatMessage(messages.loadMoreLogs)}
				</TableCellMedium>
				<TableCellLarge selectedTab={selectedTab} />
			</TableRow>
		);
	}
	const { node } = edgeData[virtualRowIndex];
	const getDetailsCell = (
		details:
			| {
					readonly errorKey: string | null | undefined;
					readonly mailboxMessage: string | null | undefined;
			  }
			| null
			| undefined,
		tabSelected: string,
	) => {
		let errorMessageComponent: ReactNode;
		if (details) {
			errorMessageComponent = (
				<ErrorMessage
					errorKey={details.errorKey}
					projectKey={projectKey}
					mailboxMessage={details.mailboxMessage}
					formatMessage={formatMessage}
					tabSelected={tabSelected}
				/>
			);
		}
		const TooltipComponent = forwardRef<HTMLDivElement, TooltipPrimitiveProps>((props, ref) => (
			<InlineDialog {...props} ref={ref} />
		));

		return (
			<LogsDetails selectedTab={selectedTab}>
				<Tooltip
					position="bottom-start"
					component={TooltipComponent}
					content={errorMessageComponent}
				>
					{(tooltipProps) => (
						<span
							data-testid="spa-apps-servicedesk-notification-logs.ui.notification-logs-page.notification-logs-tab-layout.notification-tabs.notification-table.logs-table.span"
							aria-label="tooltip"
							{...tooltipProps}
						>
							{errorMessageComponent}
						</span>
					)}
				</Tooltip>
			</LogsDetails>
		);
	};
	const getNotificationTypeCell = (notificationType: string | null | undefined) => {
		if (!notificationType) return null;
		const notificationName = getNotificationName(notificationType, formatMessage);
		const TooltipComponent = forwardRef<HTMLDivElement, TooltipPrimitiveProps>((props, ref) => (
			<InlineDialog {...props} ref={ref} />
		));
		return (
			<NotificationTypeWrapper>
				<Tooltip position="bottom-start" component={TooltipComponent} content={notificationName}>
					{(tooltipProps) => <span {...tooltipProps}>{notificationName}</span>}
				</Tooltip>
			</NotificationTypeWrapper>
		);
	};

	const getCustomerNameCell = (customerName: string | undefined) => {
		const TooltipComponent = forwardRef<HTMLDivElement, TooltipPrimitiveProps>((props, ref) => (
			<InlineDialog {...props} ref={ref} />
		));
		return (
			<CustomerNameWrapper>
				<Tooltip position="bottom-start" component={TooltipComponent} content={customerName}>
					{(tooltipProps) => <span {...tooltipProps}>{customerName}</span>}
				</Tooltip>
			</CustomerNameWrapper>
		);
	};

	return (
		<>
			{edgeData.length && (
				<TableRow
					data-index={virtualRowIndex}
					height={virtualRow.size}
					translateY={virtualRow.start}
					key={virtualRowIndex}
				>
					<TableCellSmall selectedTab={selectedTab}>
						{getLogDateTime(node?.actionTimestamp, timeZone)}
					</TableCellSmall>
					{selectedTab === issueFailuresTab && (
						<TableCellSmall selectedTab={selectedTab}>
							{getNotificationTypeCell(node?.notificationActionType)}
						</TableCellSmall>
					)}
					<TableCellMedium selectedTab={selectedTab}>
						{getCustomerNameCell(node?.recipient?.email || node?.recipient?.name)}
					</TableCellMedium>
					{selectedTab === issueFailuresTab && (
						<TableCellSmaller selectedTab={selectedTab}>
							<a target="_blank" href={node?.issueContext?.issue?.webUrl || ''}>
								{node?.issueContext?.issue?.key}
							</a>
						</TableCellSmaller>
					)}
					<TableCellLarge selectedTab={selectedTab}>
						{getDetailsCell(node?.notificationDetails, selectedTab)}
					</TableCellLarge>
				</TableRow>
			)}
		</>
	);
};

export const TableHeadForEmptyRecords = ({
	selectedTab,
	children,
}: {
	selectedTab: string;
	children: ReactNode;
}) => {
	const { formatMessage } = useIntl();
	return (
		<table>
			<thead>
				<tr>
					<TableHeaderCellSmall selectedTab={selectedTab}>
						{formatMessage(messages.tableHeaderDateTime)}
					</TableHeaderCellSmall>
					{selectedTab === issueFailuresTab && (
						<TableHeaderCellSmall selectedTab={selectedTab}>
							{formatMessage(messages.tableHeaderNotificationName)}
						</TableHeaderCellSmall>
					)}
					<TableHeaderCellMedium selectedTab={selectedTab}>
						{formatMessage(messages.tableHeaderCustomerEmail)}
					</TableHeaderCellMedium>
					{selectedTab === issueFailuresTab && (
						<TableHeaderCellSmaller selectedTab={selectedTab}>
							{formatMessage(messages.ticketKey)}
						</TableHeaderCellSmaller>
					)}
					<TableHeaderCellLarge selectedTab={selectedTab}>
						{formatMessage(messages.tableHeaderErrorMessage)}
					</TableHeaderCellLarge>
				</tr>
			</thead>
			{children}
		</table>
	);
};

export const ShowTable = <
	TScrollElement extends Element = Element,
	TItemElement extends Element = Element,
>({
	data,
	virtualizer,
	projectKey,
	outgoingMailConfigData,
	selectedTab,
}: ShowTablePropsType<TScrollElement, TItemElement>) => {
	const { formatMessage } = useIntl();
	const {
		data: { user },
	} = // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		useCurrentUser() as { data: { user: DataBasic & User } };
	const timeZone = typeof user.timeZone === 'string' ? user.timeZone : '';
	const { outgoingMailConfig, error, loading } = outgoingMailConfigData;

	// when we don't have customer logs i.e 0 logs
	if (!data.length && !error && !loading && outgoingMailConfig !== null) {
		if (!outgoingMailConfig?.enabled) {
			return (
				<>
					<FireTrackAnalytics eventName="LogsTableInTabsLayout RenderOutgoingMailConfigDisabledTextAndEmptyTable" />
					<TableHeadForEmptyRecords selectedTab={selectedTab}>
						<NoDataEnableOutgoingMailInfo />
					</TableHeadForEmptyRecords>
				</>
			);
		}
		return (
			<>
				<FireTrackAnalytics eventName="LogsTableInTabsLayout RenderEmptyTable" />
				<TableHeadForEmptyRecords selectedTab={selectedTab}>
					<NoRecords />
				</TableHeadForEmptyRecords>
			</>
		);
	}

	if (data.length) {
		// when we have customer logs show table of records
		return (
			<>
				<FireTrackAnalytics eventName="LogsTableInTabsLayout RenderTableWithData" />
				<InfiniteScrollTable
					totalSize={virtualizer.getTotalSize()}
					data-testid="spa-apps-servicedesk-notification-logs.ui.notification-logs-page.notification-logs-tab-layout.notification-tabs.notification-table.logs-table.infinite-scroll-table"
				>
					<Thead>
						<tr>
							<TableHeaderCellSmall selectedTab={selectedTab}>
								{formatMessage(messages.tableHeaderDateTime)}
							</TableHeaderCellSmall>
							{selectedTab === issueFailuresTab && (
								<TableHeaderCellSmall selectedTab={selectedTab}>
									{formatMessage(messages.tableHeaderNotificationName)}
								</TableHeaderCellSmall>
							)}
							<TableHeaderCellMedium selectedTab={selectedTab}>
								{formatMessage(messages.tableHeaderCustomerEmail)}
							</TableHeaderCellMedium>
							{selectedTab === issueFailuresTab && (
								<TableHeaderCellSmaller selectedTab={selectedTab}>
									{formatMessage(messages.ticketKey)}
								</TableHeaderCellSmaller>
							)}
							<TableHeaderCellLarge selectedTab={selectedTab}>
								{formatMessage(messages.tableHeaderErrorMessage)}
							</TableHeaderCellLarge>
						</tr>
					</Thead>
					<TableBody data-testid="spa-apps-servicedesk-notification-logs.ui.notification-logs-page.notification-logs-tab-layout.notification-tabs.notification-table.logs-table.table-body">
						{virtualizer
							.getVirtualItems()
							.map((virtualRow) =>
								rowRenderer(virtualRow, data, formatMessage, projectKey, selectedTab, timeZone),
							)}
					</TableBody>
				</InfiniteScrollTable>
			</>
		);
	}

	return null;
};

export const LogsTable = <
	TScrollElement extends Element = Element,
	TItemElement extends Element = Element,
>({
	allEdges,
	virtualizer,
	projectKey,
	outgoingMailConfigData,
	selectedTab,
}: LogsTablePropsType<TScrollElement, TItemElement>) => {
	const LogsDataFragment = graphql`
		fragment logsTableOfTabs_logEdges on AquaOutgoingEmailLogItemEdge @relay(plural: true) {
			node {
				issueContext {
					issue {
						key
						webUrl
					}
				}
				notificationActionType
				actionTimestamp
				notificationDetails {
					errorKey
					mailboxMessage
				}
				recipient {
					... on AtlassianAccountUser {
						email
					}
					... on CustomerUser {
						name
					}
				}
			}
		}
	`;

	const data = useFragment<logsTableOfTabs_logEdges$key>(LogsDataFragment, allEdges);

	return (
		<ShowTable
			data={data}
			virtualizer={virtualizer}
			projectKey={projectKey}
			outgoingMailConfigData={outgoingMailConfigData}
			selectedTab={selectedTab}
		/>
	);
};

const issueFailureTab = 'ISSUE_NOTIFICATION';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LogsDetails = styled.div<{ selectedTab: string }>({
	display: 'block',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ selectedTab }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		selectedTab === issueFailureTab ? `${gridSize * 75}px` : `${gridSize * 87.5}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CustomerNameWrapper = styled.div({
	display: 'block',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 33.75}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NotificationTypeWrapper = styled.div({
	display: 'block',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 33.75}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableCellSmaller = styled.td<{ selectedTab: string }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ selectedTab }) => (selectedTab === issueFailureTab ? '10%' : '15%'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableCellSmall = styled.td<{ selectedTab: string }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ selectedTab }) => (selectedTab === issueFailureTab ? '11%' : '15%'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableCellMedium = styled.td<{ selectedTab: string }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ selectedTab }) => (selectedTab === issueFailureTab ? '15%' : '25%'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableCellLarge = styled.td<{ selectedTab: string }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ selectedTab }) => (selectedTab === issueFailureTab ? '44%' : '50%'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Thead = styled.thead({
	top: 0,
	position: 'sticky',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: layers.card,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N800),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableHeaderCellSmaller = styled.th<{ selectedTab: string }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ selectedTab }) => (selectedTab === issueFailureTab ? '10%' : '15%'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
	position: 'sticky',
	fontWeight: 'normal',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableHeaderCellSmall = styled.th<{ selectedTab: string }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ selectedTab }) => (selectedTab === issueFailureTab ? '11%' : '15%'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
	position: 'sticky',
	fontWeight: 'normal',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableHeaderCellMedium = styled.th<{ selectedTab: string }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ selectedTab }) => (selectedTab === issueFailureTab ? '15%' : '25%'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
	position: 'sticky',
	fontWeight: 'normal',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableHeaderCellLarge = styled.th<{ selectedTab: string }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ selectedTab }) => (selectedTab === issueFailureTab ? '44%' : '50%'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
	position: 'sticky',
	fontWeight: 'normal',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableRow = styled.tr<{ height: number; translateY: number }>({
	position: 'absolute',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ height }) => `${height}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	transform: ({ translateY }) => `translateY(${translateY}px)`,
	marginTop: token('space.100', '8px'),
	marginBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableBody = styled.tbody({
	height: 'inherit',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InfiniteScrollTable = styled.table<{ totalSize: number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ totalSize }) => `${totalSize}px`,
	width: '100%',
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineDialog = styled(TooltipPrimitive)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('elevation.surface', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	boxShadow: token(
		'elevation.shadow.overlay',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`0 4px 8px -2px ${colors.N50A}, 0 0 1px ${colors.N60A}`,
	),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 40}px`,
	boxSizing: 'content-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	padding: `${gridSize * 2}px ${gridSize * 3}px`,
	wordBreak: 'break-word',
});
